import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ShoppingBag, ArrowLeft, ArrowRight, Trash2, Plus, Minus, Check, ShieldCheck, Truck, Package, Home as HomeIcon, Tag } from "lucide-react";

const CATEGORY_LABELS = {
  vetements: "Vêtements",
  batons: "Bâtons",
  accessoires: "Accessoires",
};
const CATEGORY_FILTERS = [
  { value: "all", label: "Tout voir" },
  { value: "vetements", label: "Vêtements" },
  { value: "batons", label: "Bâtons" },
  { value: "accessoires", label: "Accessoires" },
];

const CART_KEY = "nyb_shop_cart_v1";

function loadCart() {
  try {
    return JSON.parse(localStorage.getItem(CART_KEY) || "[]");
  } catch { return []; }
}
function saveCart(items) {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
  window.dispatchEvent(new Event("cart:changed"));
}

function useCart() {
  const [items, setItems] = useState(loadCart());
  useEffect(() => {
    const h = () => setItems(loadCart());
    window.addEventListener("cart:changed", h);
    window.addEventListener("storage", h);
    return () => { window.removeEventListener("cart:changed", h); window.removeEventListener("storage", h); };
  }, []);
  const add = (product, qty = 1) => {
    const next = [...loadCart()];
    const i = next.findIndex(x => x.product_id === product.id);
    if (i >= 0) next[i].quantity = Math.min(99, next[i].quantity + qty);
    else next.push({ product_id: product.id, name: product.name, price_eur: product.price_eur, image_url: product.image_url, category: product.category, quantity: qty });
    saveCart(next);
    toast.success(`« ${product.name} » ajouté au panier`);
  };
  const remove = (product_id) => { saveCart(loadCart().filter(x => x.product_id !== product_id)); };
  const setQty = (product_id, qty) => {
    const next = loadCart().map(x => x.product_id === product_id ? { ...x, quantity: Math.max(1, Math.min(99, qty)) } : x);
    saveCart(next);
  };
  const clear = () => saveCart([]);
  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.quantity * i.price_eur, 0);
  return { items, add, remove, setQty, clear, count, subtotal };
}

function CartBadge() {
  const { count } = useCart();
  if (count === 0) return null;
  return (
    <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[20px] rounded-full bg-[var(--terracotta)] text-white text-[11px] px-1.5" data-testid="cart-badge">{count}</span>
  );
}

function ProductCard({ p, onAdd }) {
  return (
    <div className="card-warm overflow-hidden flex flex-col" data-testid={`product-card-${p.id}`}>
      <div className="aspect-[4/3] bg-[var(--forest-light)] overflow-hidden">
        {p.image_url ? (
          <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--muted-text)]"><Package size={44} /></div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <span className="overline text-xs">{CATEGORY_LABELS[p.category] || p.category}</span>
        <h3 className="font-serif-display text-xl mt-1" style={{ color: "var(--pine)" }}>{p.name}</h3>
        {p.description && <p className="text-sm text-[var(--muted-text)] mt-2 line-clamp-3">{p.description}</p>}
        <div className="mt-auto pt-4 flex items-center justify-between">
          <span className="font-semibold text-lg" style={{ color: "var(--terracotta)" }}>{p.price_eur.toFixed(2)} €</span>
          <button onClick={() => onAdd(p)} className="btn-secondary text-sm" data-testid={`product-add-${p.id}`}>
            <Plus size={14}/> Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

function ShopHeader() {
  return (
    <header className="fixed top-0 inset-x-0 z-50" style={{ background: "rgba(249,246,240,0.9)", backdropFilter: "blur(12px)", borderBottom: "1px solid rgba(46,90,68,0.08)" }}>
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-4">
        <Link to="/" className="font-serif-display text-lg sm:text-2xl font-semibold" style={{ color: "var(--pine)" }} data-testid="shop-logo">
          Nordic <span style={{ color: "var(--terracotta)" }}>Yoga Belgium</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/" className="btn-ghost text-sm inline-flex items-center gap-1.5 border" data-testid="shop-back-home"><HomeIcon size={14}/> Accueil</Link>
          <Link to="/boutique/panier" className="btn-primary text-sm inline-flex items-center gap-1.5" data-testid="shop-cart-link">
            <ShoppingBag size={16}/> Panier <CartBadge />
          </Link>
        </div>
      </div>
    </header>
  );
}

function ShopBrowse() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [settings, setSettings] = useState(null);
  const cart = useCart();
  useEffect(() => {
    api.get("/shop/products").then(r => setProducts(r.data)).catch(() => setProducts([]));
    api.get("/shop/settings").then(r => setSettings(r.data)).catch(() => {});
  }, []);
  const filtered = filter === "all" ? products : products.filter(p => p.category === filter);
  return (
    <div className="min-h-screen" style={{ background: "var(--sand)" }}>
      <ShopHeader />
      <section className="pt-28 pb-14 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <span className="overline">Boutique</span>
            <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold mt-2" style={{ color: "var(--pine)" }}>Vêtements & équipements</h1>
            <p className="mt-3 text-[var(--muted-text)] max-w-xl">Tout ce qu'il vous faut pour vos sorties Nordic Yoga® et Marche Nordique. Paiement en espèces, QR Code ou virement — retrait sur place ou livraison postale.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap mb-8" data-testid="shop-filters">
          {CATEGORY_FILTERS.map(f => (
            <button key={f.value} onClick={() => setFilter(f.value)}
              className={`px-4 py-1.5 rounded-full text-sm transition ${filter === f.value ? "bg-[var(--pine)] text-white" : "bg-white text-[var(--muted-text)] border border-slate-200 hover:border-[var(--forest)]"}`}
              data-testid={`shop-filter-${f.value}`}>
              {f.label}
            </button>
          ))}
        </div>
        {filtered.length === 0 ? (
          <div className="text-center text-[var(--muted-text)] py-24 card-warm">
            Aucun produit disponible dans cette catégorie pour l'instant.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(p => <ProductCard key={p.id} p={p} onAdd={cart.add} />)}
          </div>
        )}
      </section>
      {settings && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 grid md:grid-cols-3 gap-4 text-sm">
          <div className="card-warm p-5"><Truck className="text-[var(--terracotta)] mb-2" size={20}/>
            <div className="font-semibold" style={{ color: "var(--pine)" }}>Livraison postale</div>
            <div className="text-[var(--muted-text)] mt-1">{settings.shipping_fee_eur.toFixed(2)} € — offerte dès {settings.free_shipping_threshold_eur.toFixed(0)} €</div>
          </div>
          <div className="card-warm p-5"><Package className="text-[var(--terracotta)] mb-2" size={20}/>
            <div className="font-semibold" style={{ color: "var(--pine)" }}>Retrait sur place</div>
            <div className="text-[var(--muted-text)] mt-1">Lors d'une prochaine séance — gratuit.</div>
          </div>
          <div className="card-warm p-5"><ShieldCheck className="text-[var(--terracotta)] mb-2" size={20}/>
            <div className="font-semibold" style={{ color: "var(--pine)" }}>Paiement flexible</div>
            <div className="text-[var(--muted-text)] mt-1">Espèces · QR Code · Virement bancaire</div>
          </div>
        </section>
      )}
    </div>
  );
}

function ShopCart() {
  const cart = useCart();
  const nav = useNavigate();
  return (
    <div className="min-h-screen" style={{ background: "var(--sand)" }}>
      <ShopHeader />
      <section className="pt-28 pb-14 max-w-4xl mx-auto px-4 sm:px-6">
        <button onClick={() => nav("/boutique")} className="btn-ghost text-sm inline-flex items-center gap-1.5 border mb-6" data-testid="cart-back-shop"><ArrowLeft size={14}/> Retour à la boutique</button>
        <h1 className="font-serif-display text-4xl font-semibold mb-6" style={{ color: "var(--pine)" }}>Mon panier</h1>
        {cart.items.length === 0 ? (
          <div className="card-warm p-12 text-center">
            <ShoppingBag size={40} className="mx-auto text-[var(--muted-text)] mb-4" />
            <p className="text-[var(--muted-text)]">Votre panier est vide.</p>
            <Link to="/boutique" className="btn-primary text-sm mt-6 inline-flex" data-testid="cart-empty-shop">Découvrir la boutique</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-4">
              {cart.items.map(it => (
                <div key={it.product_id} className="card-warm p-4 flex items-center gap-4" data-testid={`cart-item-${it.product_id}`}>
                  <div className="w-20 h-20 rounded-lg bg-[var(--forest-light)] overflow-hidden shrink-0">
                    {it.image_url ? <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[var(--muted-text)]"><Package/></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase text-[var(--muted-text)]">{CATEGORY_LABELS[it.category] || it.category}</div>
                    <div className="font-serif-display text-lg truncate" style={{ color: "var(--pine)" }}>{it.name}</div>
                    <div className="text-sm text-[var(--muted-text)]">{it.price_eur.toFixed(2)} € l'unité</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => cart.setQty(it.product_id, it.quantity - 1)} className="btn-ghost border w-8 h-8 inline-flex items-center justify-center" data-testid={`cart-dec-${it.product_id}`}><Minus size={14}/></button>
                    <span className="w-8 text-center font-semibold">{it.quantity}</span>
                    <button onClick={() => cart.setQty(it.product_id, it.quantity + 1)} className="btn-ghost border w-8 h-8 inline-flex items-center justify-center" data-testid={`cart-inc-${it.product_id}`}><Plus size={14}/></button>
                  </div>
                  <div className="font-semibold text-[var(--terracotta)] w-20 text-right">{(it.price_eur * it.quantity).toFixed(2)} €</div>
                  <button onClick={() => cart.remove(it.product_id)} className="text-red-600 hover:text-red-700" data-testid={`cart-remove-${it.product_id}`}><Trash2 size={16}/></button>
                </div>
              ))}
            </div>
            <aside className="card-warm p-6 h-fit sticky top-24">
              <h3 className="font-semibold text-lg" style={{ color: "var(--pine)" }}>Récapitulatif</h3>
              <div className="mt-4 text-sm space-y-2">
                <div className="flex justify-between"><span>Sous-total</span><span>{cart.subtotal.toFixed(2)} €</span></div>
                <div className="flex justify-between text-[var(--muted-text)] text-xs">Livraison calculée à l'étape suivante</div>
              </div>
              <button onClick={() => nav("/boutique/commande")} className="btn-primary w-full mt-6" data-testid="cart-checkout-btn">
                Passer à la commande <ArrowRight size={16}/>
              </button>
              <button onClick={cart.clear} className="text-xs text-red-600 hover:underline mt-3 mx-auto block" data-testid="cart-clear-btn">Vider le panier</button>
            </aside>
          </div>
        )}
      </section>
    </div>
  );
}

function ShopCheckout() {
  const cart = useCart();
  const nav = useNavigate();
  const [settings, setSettings] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    delivery_method: "pickup", address: "", zip_code: "", city: "", notes: "",
    payment_method: "cash",
  });
  const [discountCode, setDiscountCode] = useState("");
  const [applied, setApplied] = useState(null); // { code, discount_amount_eur }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/shop/settings").then(r => setSettings(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!submitted && cart.items.length === 0) nav("/boutique/panier", { replace: true });
  }, [cart.items.length, nav, submitted]);

  const shippingFee = useMemo(() => {
    if (!settings || form.delivery_method !== "postal") return 0;
    if (settings.free_shipping_threshold_eur > 0 && cart.subtotal >= settings.free_shipping_threshold_eur) return 0;
    return settings.shipping_fee_eur;
  }, [settings, form.delivery_method, cart.subtotal]);
  const discountAmount = applied?.discount_amount_eur || 0;
  const total = Math.max(0, cart.subtotal + shippingFee - discountAmount);

  const applyDiscount = async () => {
    if (!discountCode.trim()) return;
    try {
      const { data } = await api.post("/shop/discount/validate", { code: discountCode.trim(), subtotal_eur: cart.subtotal });
      setApplied(data);
      toast.success(`Code « ${data.code} » appliqué`);
    } catch (e) {
      setApplied(null);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        items: cart.items.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
        discount_code: applied?.code || "",
      };
      const { data } = await api.post("/shop/orders", payload);
      setSubmitted(true);
      cart.clear();
      nav(`/boutique/merci?n=${encodeURIComponent(data.order_number)}&t=${data.total_eur.toFixed(2)}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur lors de la commande");
    } finally { setBusy(false); }
  };

  if (!submitted && cart.items.length === 0) return null;

  return (
    <div className="min-h-screen" style={{ background: "var(--sand)" }}>
      <ShopHeader />
      <section className="pt-28 pb-14 max-w-5xl mx-auto px-4 sm:px-6">
        <button onClick={() => nav("/boutique/panier")} className="btn-ghost text-sm inline-flex items-center gap-1.5 border mb-6" data-testid="checkout-back-cart"><ArrowLeft size={14}/> Retour au panier</button>
        <h1 className="font-serif-display text-4xl font-semibold mb-8" style={{ color: "var(--pine)" }}>Finaliser la commande</h1>
        <form onSubmit={submit} className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6">
            <div className="card-warm p-6">
              <h3 className="font-semibold mb-4" style={{ color: "var(--pine)" }}>Vos coordonnées</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <input required className="input-field" placeholder="Nom complet *" value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} data-testid="checkout-name" />
                <input required type="email" className="input-field" placeholder="Email *" value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })} data-testid="checkout-email" />
                <input className="input-field sm:col-span-2" placeholder="Téléphone (optionnel)" value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} data-testid="checkout-phone" />
              </div>
            </div>
            <div className="card-warm p-6">
              <h3 className="font-semibold mb-4" style={{ color: "var(--pine)" }}>Mode de livraison</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className={`p-4 rounded-lg border cursor-pointer ${form.delivery_method === "pickup" ? "border-[var(--pine)] bg-[var(--forest-light)]" : "border-slate-200 bg-white"}`}>
                  <input type="radio" name="delivery" value="pickup" checked={form.delivery_method === "pickup"} onChange={e => setForm({ ...form, delivery_method: e.target.value })} className="mr-2" data-testid="checkout-delivery-pickup" />
                  <strong>Retrait sur place</strong> — Gratuit
                  <div className="text-xs text-[var(--muted-text)] mt-1">Lors d'une prochaine séance</div>
                </label>
                <label className={`p-4 rounded-lg border cursor-pointer ${form.delivery_method === "postal" ? "border-[var(--pine)] bg-[var(--forest-light)]" : "border-slate-200 bg-white"}`}>
                  <input type="radio" name="delivery" value="postal" checked={form.delivery_method === "postal"} onChange={e => setForm({ ...form, delivery_method: e.target.value })} className="mr-2" data-testid="checkout-delivery-postal" />
                  <strong>Livraison postale</strong>{settings ? ` — ${settings.shipping_fee_eur.toFixed(2)} €` : ""}
                  <div className="text-xs text-[var(--muted-text)] mt-1">{settings?.free_shipping_threshold_eur > 0 ? `Offerte dès ${settings.free_shipping_threshold_eur.toFixed(0)} €` : ""}</div>
                </label>
              </div>
              {form.delivery_method === "postal" && (
                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  <input required className="input-field sm:col-span-2" placeholder="Adresse *" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="checkout-address" />
                  <input required className="input-field" placeholder="Code postal *" value={form.zip_code} onChange={e => setForm({ ...form, zip_code: e.target.value })} data-testid="checkout-zip" />
                  <input required className="input-field" placeholder="Ville *" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} data-testid="checkout-city" />
                </div>
              )}
            </div>
            <div className="card-warm p-6">
              <h3 className="font-semibold mb-4" style={{ color: "var(--pine)" }}>Paiement</h3>
              <select className="input-field" value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })} data-testid="checkout-payment">
                <option value="cash">Espèces</option>
                <option value="qr">QR Code (Bancontact)</option>
                <option value="transfer">Virement bancaire</option>
              </select>
              <textarea className="input-field mt-3" rows={3} placeholder="Remarque (optionnel)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="checkout-notes" />
            </div>
          </div>
          <aside className="card-warm p-6 h-fit sticky top-24 space-y-3">
            <h3 className="font-semibold" style={{ color: "var(--pine)" }}>Récapitulatif</h3>
            <div className="text-sm space-y-1 pb-3 border-b border-slate-100">
              {cart.items.map(i => (
                <div key={i.product_id} className="flex justify-between text-[var(--muted-text)]">
                  <span className="truncate mr-2">{i.name} × {i.quantity}</span>
                  <span>{(i.price_eur * i.quantity).toFixed(2)} €</span>
                </div>
              ))}
            </div>
            <div className="text-sm space-y-1.5">
              <div className="flex justify-between"><span>Sous-total</span><span>{cart.subtotal.toFixed(2)} €</span></div>
              <div className="flex justify-between"><span>Livraison</span><span>{shippingFee.toFixed(2)} €</span></div>
              {applied && (
                <div className="flex justify-between text-[var(--terracotta)]"><span>Remise ({applied.code})</span><span>- {discountAmount.toFixed(2)} €</span></div>
              )}
              <div className="flex justify-between font-semibold text-lg pt-2 border-t border-slate-100" style={{ color: "var(--pine)" }}>
                <span>Total</span><span>{total.toFixed(2)} €</span>
              </div>
            </div>
            <div className="pt-3 border-t border-slate-100">
              <label className="text-xs text-[var(--muted-text)] inline-flex items-center gap-1"><Tag size={12}/> Code de remise</label>
              <div className="flex gap-2 mt-1">
                <input className="input-field flex-1 uppercase" placeholder="CODE" value={discountCode} onChange={e => setDiscountCode(e.target.value.toUpperCase())} data-testid="checkout-discount-input" />
                <button type="button" onClick={applyDiscount} className="btn-secondary text-sm" data-testid="checkout-discount-apply">Appliquer</button>
              </div>
              {applied && <p className="text-xs text-[var(--forest)] mt-1"><Check size={12} className="inline"/> Code appliqué</p>}
            </div>
            <button type="submit" disabled={busy} className="btn-primary w-full mt-4 disabled:opacity-50" data-testid="checkout-submit">
              {busy ? "Envoi…" : "Valider ma commande"}
            </button>
            <p className="text-[10px] text-[var(--muted-text)] leading-tight mt-2">Aucun débit en ligne — vous réglez sur place, par QR Code ou virement une fois la commande confirmée par email.</p>
          </aside>
        </form>
      </section>
    </div>
  );
}

function ShopThankYou() {
  const [sp] = useSearchParams();
  const n = sp.get("n") || "";
  const t = sp.get("t") || "";
  return (
    <div className="min-h-screen" style={{ background: "var(--sand)" }}>
      <ShopHeader />
      <section className="pt-32 pb-20 max-w-3xl mx-auto px-4 sm:px-6 text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[var(--forest-light)] mb-6">
          <Check size={40} className="text-[var(--forest)]"/>
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold" style={{ color: "var(--pine)" }}>Merci pour votre commande !</h1>
        <p className="mt-4 text-[var(--muted-text)]">Votre commande <strong>{n}</strong>{t && <> d'un montant de <strong>{t} €</strong></>} a bien été enregistrée.</p>
        <p className="mt-2 text-[var(--muted-text)]">Un email de confirmation vient de vous être envoyé avec les instructions de paiement et de livraison.</p>
        <div className="mt-8 flex gap-3 justify-center flex-wrap">
          <Link to="/boutique" className="btn-secondary" data-testid="thanks-back-shop">Retour à la boutique</Link>
          <Link to="/" className="btn-primary" data-testid="thanks-back-home">Accueil</Link>
        </div>
      </section>
    </div>
  );
}

export default function Shop() {
  const location = useLocation();
  const path = location.pathname;
  if (path.startsWith("/boutique/panier")) return <ShopCart />;
  if (path.startsWith("/boutique/commande")) return <ShopCheckout />;
  if (path.startsWith("/boutique/merci")) return <ShopThankYou />;
  return <ShopBrowse />;
}
